<template>
  <div v-if="hasVisibleHealthIssues" class="integration-health-banner">
    <v-alert
      v-for="integration in unhealthyIntegrations"
      :key="`${integration.integrationType}-${integration.integrationName}`"
      :type="getAlertType(integration.health.status)"
      dismissible
      prominent
      class="mb-2"
      @input="dismissAlert(integration)"
    >
      <template v-slot:prepend>
        <v-icon>{{ getStatusIcon(integration.health.status) }}</v-icon>
      </template>
      
      <div class="d-flex align-center justify-space-between">
        <div class="flex-grow-1">
          <div class="font-weight-bold mb-1">
            {{ integration.integrationName }} Integration Issue
          </div>
          <div class="text-body-2">
            {{ getHealthMessage(integration) }}
          </div>
          <div v-if="integration.health.lastSuccessful" class="text-caption text--secondary mt-1">
            Last successful connection: {{ formatDate(integration.health.lastSuccessful) }}
          </div>
        </div>
        
        <div class="ml-4">
          <v-btn
            small
            text
            color="primary"
            @click="viewDetails(integration)"
          >
            <v-icon small left>mdi-information</v-icon>
            View Details
          </v-btn>
        </div>
      </div>
    </v-alert>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { NotificationService, NOTIFICATION_TYPES } from '@/services/notification.service';

export default {
  name: 'IntegrationHealthBanner',
  
  data() {
    return {};
  },

  computed: {
    ...mapGetters([
      'hasVisibleHealthIssues',
      'unhealthyIntegrations',
    ]),
  },

  methods: {

    getAlertType(status) {
      switch (status) {
        case 'error':
          return 'error';
        case 'warning':
          return 'warning';
        default:
          return 'info';
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case 'error':
          return 'mdi-alert-circle';
        case 'warning':
          return 'mdi-alert';
        default:
          return 'mdi-information';
      }
    },

    getHealthMessage(integration) {
      const health = integration.health;
      
      if (health.errorMessage) {
        return health.errorMessage;
      }
      
      switch (health.status) {
        case 'error':
          return `Your ${integration.integrationName} integration is experiencing connection issues. This may affect data synchronization.`;
        case 'warning':
          return `Your ${integration.integrationName} integration has some issues that may affect performance.`;
        default:
          return `Your ${integration.integrationName} integration status needs attention.`;
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffHours < 1) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return `${diffMinutes} minutes ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hours ago`;
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString();
      }
    },

    dismissAlert(integration) {
      this.$store.dispatch('dismissHealthAlert', {
        integrationType: integration.integrationType,
        integrationName: integration.integrationName,
      });
    },

    viewDetails(integration) {
      NotificationService.alert({
        title: 'Integration Details',
        message: `
          <strong>Integration:</strong> ${integration.integrationName}<br>
          <strong>Status:</strong> ${integration.health.status}<br>
          <strong>Error Count:</strong> ${integration.health.errorCount || 0}<br>
          <strong>Last Checked:</strong> ${this.formatDate(integration.health.lastChecked)}
        `,
        type: NOTIFICATION_TYPES.INFO,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.integration-health-banner {
  position: sticky;
  top: 0;
  z-index: 1000;
  max-width: 100%;
}

.v-alert {
  border-radius: 8px;
  width: 100%;
  max-width: none;
  
  &.error--text {
    background-color: rgba(244, 67, 54, 0.1);
    border-left: 4px solid #f44336;
  }
  
  &.warning--text {
    background-color: rgba(255, 152, 0, 0.1);
    border-left: 4px solid #ff9800;
  }
}

.text-body-2 {
  line-height: 1.4;
}

.text-caption {
  opacity: 0.7;
}

// Ensure full width content
.d-flex {
  width: 100%;
}
</style> 